import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, Gavel, ShieldAlert, Zap } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import SportsbookReportCard from '../../components/admin/SportsbookReportCard';
import ManualGradePanel from '../../components/admin/ManualGradePanel';
import FixGradePanel from '../../components/admin/FixGradePanel';
import BoostsAdminPanel from '../../components/admin/BoostsAdminPanel';
import { SportsRiskPanel } from './SportsRiskPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// One-stop Sportsbook admin hub, split into Report / Grading / Limits sub-tabs.
// The Grading tab carries a live count badge of tickets awaiting manual grading
// so nothing ever gets silently stuck.
export default function SportsAdminTab() {
    const [pendingCount, setPendingCount] = useState(0);

    const fetchPending = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/sports/manual-grade/pending`, { withCredentials: true });
            setPendingCount(data?.count ?? (data?.bets?.length || 0));
        } catch { /* non-blocking */ }
    }, []);

    useEffect(() => {
        fetchPending();
        const t = setInterval(fetchPending, 30000);
        return () => clearInterval(t);
    }, [fetchPending]);

    return (
        <div data-testid="sports-admin-tab">
            <Tabs defaultValue="report" className="w-full">
                <TabsList className="bg-[#0a0a0a] border border-white/10 mb-5 flex-wrap h-auto">
                    <TabsTrigger value="report" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black" data-testid="sports-subtab-report">
                        <TrendingUp className="w-4 h-4 mr-1.5" /> Report
                    </TabsTrigger>
                    <TabsTrigger value="grading" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black relative" data-testid="sports-subtab-grading">
                        <Gavel className="w-4 h-4 mr-1.5" /> Grading
                        {pendingCount > 0 && (
                            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold" data-testid="sports-grading-badge">
                                {pendingCount}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="limits" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black" data-testid="sports-subtab-limits">
                        <ShieldAlert className="w-4 h-4 mr-1.5" /> Limits
                    </TabsTrigger>
                    <TabsTrigger value="boosts" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black" data-testid="sports-subtab-boosts">
                        <Zap className="w-4 h-4 mr-1.5" /> Boosts
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="report">
                    <SportsbookReportCard />
                </TabsContent>
                <TabsContent value="grading">
                    <div className="space-y-4">
                        <ManualGradePanel onChanged={fetchPending} />
                        <FixGradePanel />
                    </div>
                </TabsContent>
                <TabsContent value="limits">
                    <SportsRiskPanel />
                </TabsContent>
                <TabsContent value="boosts">
                    <BoostsAdminPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}
